import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import {
  ICaptureDevice,
  ICaptureDevicePayload,
  ICEchoTestResult,
  IScpInfo,
  IWatchFolderMount,
  IWatchFolderMountPayload,
} from '@dataTypes/dicom-capture.type'

const CLINIC_BUSINESS_API = getEnv('REACT_APP_BUSINESS_LAYER_CLINIC')
const BASE = `${CLINIC_BUSINESS_API}/api/v1/clinic/dicom-capture`

interface ApiEnvelope<T> {
  statusCode: number
  message: string
  data: T
}

// ── SCP info ──────────────────────────────────────────────────────────────────

export const fetchScpInfoApi = async (): Promise<
  ApiEnvelope<{ scp_info: IScpInfo }>
> => {
  const response = await axios.get<ApiEnvelope<{ scp_info: IScpInfo }>>(
    `${BASE}/scp-info`,
  )
  return response.data
}

// ── Capture devices ────────────────────────────────────────────────────────────

export const fetchCaptureDevicesApi = async (
  clinicId: string,
): Promise<ApiEnvelope<{ capture_devices: ICaptureDevice[] }>> => {
  const response = await axios.get<
    ApiEnvelope<{ capture_devices: ICaptureDevice[] }>
  >(`${BASE}/capture-devices`, { params: { clinic_id: clinicId } })
  return response.data
}

export const createCaptureDeviceApi = async (
  payload: ICaptureDevicePayload,
): Promise<ApiEnvelope<{ capture_device: ICaptureDevice }>> => {
  const response = await axios.post<
    ApiEnvelope<{ capture_device: ICaptureDevice }>
  >(`${BASE}/capture-devices`, payload)
  return response.data
}

export const updateCaptureDeviceApi = async (
  id: string,
  payload: Partial<ICaptureDevicePayload>,
): Promise<ApiEnvelope<{ capture_device: ICaptureDevice }>> => {
  const response = await axios.patch<
    ApiEnvelope<{ capture_device: ICaptureDevice }>
  >(`${BASE}/capture-devices/${id}`, payload)
  return response.data
}

export const deleteCaptureDeviceApi = async (
  id: string,
): Promise<ApiEnvelope<{ capture_device_id: string }>> => {
  const response = await axios.delete<
    ApiEnvelope<{ capture_device_id: string }>
  >(`${BASE}/capture-devices/${id}`)
  return response.data
}

export const testCaptureDeviceApi = async (
  id: string,
): Promise<ApiEnvelope<ICEchoTestResult>> => {
  const response = await axios.post<ApiEnvelope<ICEchoTestResult>>(
    `${BASE}/capture-devices/${id}/test-cecho`,
  )
  return response.data
}

// ── Watch folder mounts ──────────────────────────────────────────────────────

export const fetchWatchFolderMountsApi = async (
  clinicId: string,
): Promise<ApiEnvelope<{ watch_folder_mounts: IWatchFolderMount[] }>> => {
  const response = await axios.get<
    ApiEnvelope<{ watch_folder_mounts: IWatchFolderMount[] }>
  >(`${BASE}/watch-folder-mounts`, { params: { clinic_id: clinicId } })
  return response.data
}

export const createWatchFolderMountApi = async (
  payload: IWatchFolderMountPayload,
): Promise<ApiEnvelope<{ watch_folder_mount: IWatchFolderMount }>> => {
  const response = await axios.post<
    ApiEnvelope<{ watch_folder_mount: IWatchFolderMount }>
  >(`${BASE}/watch-folder-mounts`, payload)
  return response.data
}

export const updateWatchFolderMountApi = async (
  id: string,
  payload: Partial<IWatchFolderMountPayload>,
): Promise<ApiEnvelope<{ watch_folder_mount: IWatchFolderMount }>> => {
  const response = await axios.patch<
    ApiEnvelope<{ watch_folder_mount: IWatchFolderMount }>
  >(`${BASE}/watch-folder-mounts/${id}`, payload)
  return response.data
}

export const deleteWatchFolderMountApi = async (
  id: string,
): Promise<ApiEnvelope<{ watch_folder_mount_id: string }>> => {
  const response = await axios.delete<
    ApiEnvelope<{ watch_folder_mount_id: string }>
  >(`${BASE}/watch-folder-mounts/${id}`)
  return response.data
}
